import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import FileUpload from '../components/FileUpload';
import RoleSelector from '../components/RoleSelector';
import LoadingOverlay from '../components/LoadingOverlay';

const API_BASE = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
const FEATURE_CARDS = [
  { icon: '🎯', title: 'Precision Scoring', desc: 'Section-by-section scores (0–100) powered by Gemini 2.0 Flash' },
  { icon: '🔍', title: 'Keyword Gap Analysis', desc: 'See exactly which keywords hiring managers look for' },
  { icon: '🔧', title: 'Fix Suggestions', desc: 'Concrete, actionable improvements for each section' },
  { icon: '📊', title: 'Instant Results', desc: 'Full analysis in well under a minute — often ~8 seconds' },
];

function SampleResultPreview() {
  const size = 100;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const score = 76;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;
  const arcColor = '#84cc16';

  return (
    <div className="landing-sample-card">
      <p className="landing-sample-caption">Sample analysis preview</p>
      <div className="landing-sample-inner">
        <div className="landing-score-ring-wrap" aria-hidden="true">
          <svg width={size} height={size} role="presentation">
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="#ece8e4"
              strokeWidth={stroke}
            />
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={arcColor}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="landing-score-center">
            <span className="landing-score-num">{score}</span>
            <span className="landing-score-denom">/ 100</span>
          </div>
        </div>
        <div className="landing-feedback-tags">
          <span className="landing-tag landing-tag-strong">Strong Projects</span>
          <span className="landing-tag landing-tag-warn">Missing Keywords</span>
          <span className="landing-tag landing-tag-bad">Weak Summary</span>
        </div>
      </div>
    </div>
  );
}



export default function LandingPage() {
  const [file, setFile] = useState(null);
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleAnalyze = async () => {
    if (!file) return toast.error('Please upload your resume first.');
    if (!role) return toast.error('Please select a target role.');

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('resume', file);
      formData.append('targetRole', role);

      const { data } = await axios.post(`${API_BASE}/api/resume/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      if (!data.success) throw new Error(data.error || 'Analysis failed.');

      navigate('/result', { state: { result: data.data } });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Something went wrong.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingOverlay />}

      <div className="landing-page">
        <section className="landing-hero-grid" aria-label="Roast your resume">
          <div className="landing-col-copy">
            <div className="landing-ai-badge">
              <span aria-hidden="true">🔥</span> RoastResume
            </div>

            <div>
              <p className="landing-headline-row">
              Don’t Let a Bad Resume
              
                <em>Kill a Great Candidate.</em>
              </p>
              <p className="landing-lead">
              Know exactly why your resume isn’t getting shortlisted.
              </p>

              <div className="landing-stat-pills">
                <span className="landing-stat-pill">Free to use</span>
                <span className="landing-stat-divider">|</span>
                <span className="landing-stat-pill">8 sec analysis</span>
                <span className="landing-stat-divider">|</span>
                <span className="landing-stat-pill">No login required</span>
              </div>
            </div>

           

            <SampleResultPreview />
          </div>

          <div className="landing-col-upload">
            <div className="landing-upload-card">
              <div className="landing-step-block">
                <div className="landing-step-head">
                  <span className="landing-step-pill">01</span>
                  <p className="landing-step-label">Upload your resume</p>
                </div>
                <FileUpload file={file} onFileChange={setFile} />
              </div>

              <div className="landing-step-block">
                <div className="landing-step-head">
                  <span className="landing-step-pill">02</span>
                  <p className="landing-step-label">Select target role</p>
                </div>
                <RoleSelector value={role} onChange={setRole} />
              </div>

              <button
                id="analyze-btn"
                type="button"
                onClick={handleAnalyze}
                disabled={loading || !file || !role}
                className="landing-analyze-btn"
              >
                <span aria-hidden="true">🔥</span>
                {loading ? 'Roasting…' : 'Roast My Resume'}
              </button>

              <div className="landing-trust-bar">
                <span className="landing-trust-item">
                  <span className="landing-check" aria-hidden="true">
                    ✓
                  </span>
                  PDF &amp; DOCX supported
                </span>
                <span className="landing-trust-item">
                  <span className="landing-check" aria-hidden="true">
                    ✓
                  </span>
                  Max 5 MB
                </span>
                <span className="landing-trust-item">
                  <span className="landing-check" aria-hidden="true">
                    ✓
                  </span>
                  Free · No signup
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-features-wrap" aria-labelledby="landing-features-title">
          <div className="container">
            <h2 id="landing-features-title" className="landing-section-heading">
              What you&apos;ll get
            </h2>
            <div className="landing-feature-grid">
              {FEATURE_CARDS.map((f, i) => (
                <div key={i} className="landing-feature-card fade-in">
                  <span className="landing-feature-icon">{f.icon}</span>
                  <h3 className="landing-feature-title">{f.title}</h3>
                  <p className="landing-feature-desc">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-steps-wrap" aria-labelledby="landing-how-title">
          <div className="container">
            <h2 id="landing-how-title" className="landing-section-heading">
              How it works
            </h2>
            <div className="landing-steps-row">
              {[
                { num: '1', title: 'Upload', desc: 'Drop in your PDF or DOCX resume' },
                { num: '2', title: 'Select Role', desc: 'Tell us what job you\'re targeting' },
                { num: '3', title: 'AI Roasts', desc: 'Gemini 2.0 Flash scores each section in seconds' },
                { num: '4', title: 'Improve', desc: 'Use fix suggestions to land the interview' },
              ].map((step, i) => (
                <div key={i} className="landing-step-card">
                  <div className="landing-step-card-num">{step.num}</div>
                  <h4 className="landing-step-card-title">{step.title}</h4>
                  <p className="landing-step-card-desc">{step.desc}</p>
                  {i < 3 && <div className="landing-step-arrow">→</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
